import { useCallback, useEffect, useRef, useState } from 'react'

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
export const WARNING_BEFORE_MS = 2 * 60 * 1000 // warn 2 minutes before logout
export const CHECK_INTERVAL_MS = 15 * 1000 // idle poll while no warning is showing
export const WARNING_TICK_MS = 1000 // countdown tick while the warning is showing
export const ACTIVITY_THROTTLE_MS = 10 * 1000 // cap localStorage writes under continuous input
export const LAST_ACTIVITY_KEY = 'cx.lastActivityAt'

// Deliberately excludes `mousemove`: a resting mouse on a bumped desk would renew the
// session forever, which is the exact failure this hook exists to prevent.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll'] as const

export type IdleLogoutState = {
  showWarning: boolean
  secondsRemaining: number
  extendSession: () => void
}

// Falls back to module state when localStorage is unavailable (private mode, quota).
// Idle logout then degrades to per-tab instead of cross-tab, rather than breaking.
let memoryLastActivity: number | null = null

function readLastActivity(): number | null {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY)
    if (raw === null) {
      return null
    }
    const parsed = parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return memoryLastActivity
  }
}

function writeLastActivity(at: number): void {
  memoryLastActivity = at
  try {
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at))
  } catch {
    // private mode / quota exceeded — memoryLastActivity keeps this tab working
  }
}

/**
 * Logs the user out after IDLE_TIMEOUT_MS with no interaction in *any* CurvoX tab,
 * showing a countdown warning for the last WARNING_BEFORE_MS.
 *
 * Activity is shared across tabs through a single localStorage timestamp; the `storage`
 * event propagates one tab's activity to the others.
 *
 * Idle is measured by comparing timestamps on an interval rather than by a single long
 * setTimeout, because timers do not run while the machine is suspended — a 30-minute
 * setTimeout set before a 2-hour sleep fires 2 hours later, letting the session outlive
 * the laptop being closed.
 *
 * `onLogout` is a parameter (rather than calling MSAL directly) so the hook stays
 * testable without mocking @azure/msal-react.
 */
export function useIdleLogout(onLogout: () => void): IdleLogoutState {
  const [showWarning, setShowWarning] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(WARNING_BEFORE_MS / 1000))
  const lastWriteRef = useRef(0)
  const loggedOutRef = useRef(false)
  const onLogoutRef = useRef(onLogout)

  useEffect(() => {
    onLogoutRef.current = onLogout
  }, [onLogout])

  const recordActivity = useCallback((force = false) => {
    const now = Date.now()
    if (!force && now - lastWriteRef.current < ACTIVITY_THROTTLE_MS) {
      return
    }
    lastWriteRef.current = now
    writeLastActivity(now)
    setShowWarning(false)
  }, [])

  const evaluate = useCallback(() => {
    if (loggedOutRef.current) {
      return
    }

    const now = Date.now()
    const lastActivity = readLastActivity()

    // Fail open: a missing or corrupt timestamp means "active now", never "infinitely
    // idle". Better to miss a logout than to boot someone mid-work over a bad read.
    if (lastActivity === null) {
      writeLastActivity(now)
      lastWriteRef.current = now
      setShowWarning(false)
      return
    }

    const remainingMs = IDLE_TIMEOUT_MS - (now - lastActivity)

    if (remainingMs <= 0) {
      loggedOutRef.current = true
      setShowWarning(false)
      onLogoutRef.current()
      return
    }

    if (remainingMs <= WARNING_BEFORE_MS) {
      setShowWarning(true)
      setSecondsRemaining(Math.ceil(remainingMs / 1000))
    } else {
      setShowWarning(false)
    }
  }, [])

  // Mounting the authenticated app is itself activity.
  useEffect(() => {
    recordActivity(true)
  }, [recordActivity])

  useEffect(() => {
    const onActivity = () => recordActivity()
    ACTIVITY_EVENTS.forEach(eventName => window.addEventListener(eventName, onActivity, { passive: true }))

    // A tab regaining visibility is NOT activity — the user may have been away in another
    // app past the deadline, in which case they should find themselves logged out. Re-check
    // instead, which also catches wake-from-suspend on the first frame back.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        evaluate()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const onStorage = (event: StorageEvent) => {
      if (event.key === LAST_ACTIVITY_KEY) {
        evaluate()
      }
    }
    window.addEventListener('storage', onStorage)

    return () => {
      ACTIVITY_EVENTS.forEach(eventName => window.removeEventListener(eventName, onActivity))
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('storage', onStorage)
    }
  }, [recordActivity, evaluate])

  // Tick faster while the warning is up so the countdown reads smoothly and the logout
  // lands on time; poll lazily otherwise.
  useEffect(() => {
    const intervalId = setInterval(evaluate, showWarning ? WARNING_TICK_MS : CHECK_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [evaluate, showWarning])

  const extendSession = useCallback(() => {
    recordActivity(true)
  }, [recordActivity])

  return { showWarning, secondsRemaining, extendSession }
}
